import React, { ReactNode } from "react";

import { TranslationsContainer } from "@montana/containers/translations";

import { ITranslate, ChildFunction } from "./translate";

/**
 *
 * @param value string or callback function defining which translation to render
 *
 * @example
 *  // with string as value
 *  <Translate value="login.heading" />
 *
 *  // with callback function as value
 *  <Translate value={({ login }) => login.heading}>Log ind her</Translate>
 *
 *  // with function as child
 *  <Translate value="login.heading">
 *      {translation => <h1>Heading with {translation} embedded in other text</h1>}
 *  </Translate>
 *
 * // with value as function + fallback in function body
 *  <Translate value={t => t.login.heading} defaultValue="login.heading" />
 *
 * // with function as both child and value + fallback in function body
 *  <Translate value={t => t.login.heading} defaultValue="login.heading">
 *      {t => <span>text with {t} in it</span>}
 *  </Translate>
 */

export const Translate: React.FC<ITranslate> = React.memo(({ children, value, defaultValue }: ITranslate): JSX.Element => (
    <TranslationsContainer>
        {({ getTranslation }): ReactNode => {
            let translation: string | undefined;

            let notFound: string | undefined = defaultValue;

            if (children && typeof children !== "function") {
                notFound = children as string;
            } else if (typeof value !== "function" && !defaultValue) {
                notFound = value;
            }

            if (typeof value === "function") {
                translation = getTranslation(value, notFound || "");
            } else {
                // eslint-disable-next-line @typescript-eslint/explicit-function-return-type,@typescript-eslint/no-explicit-any
                translation = getTranslation((translations: any) => value.split(".").reduce((o: any, i: any) => o[i], translations), notFound || "");
            }

            if (typeof children === "function") {
                return (children as ChildFunction)(translation);
            }

            return <>{translation}</>;
        }}
    </TranslationsContainer>
));
